import {
  assertStudioPublicationAdapter,
} from "../StudioPublicationAdapter";

export class StudioPublicationAdapterRegistryError extends Error {
  constructor(
    message,
    {
      code = "STUDIO_PUBLICATION_ADAPTER_REGISTRY_ERROR",
      details = {},
    } = {}
  ) {
    super(message);

    this.name = "StudioPublicationAdapterRegistryError";
    this.code = code;
    this.details = Object.freeze({
      ...details,
    });
  }
}

/**
 * Creates one isolated registry instance.
 *
 * Each registry owns its private Map and therefore cannot interfere
 * with another runtime or registry instance.
 */
export function createStudioPublicationAdapterRegistry({
  adapters = [],
} = {}) {
  if (!Array.isArray(adapters)) {
    throw new StudioPublicationAdapterRegistryError(
      "adapters must be an array.",
      {
        code: "INVALID_INITIAL_ADAPTERS",
      }
    );
  }

  const registrations = new Map();

  function registerPublicationAdapter(adapter) {
    assertStudioPublicationAdapter(adapter);

    const domainId = normalizeDomainId(
      adapter.domainId
    );

    if (registrations.has(domainId)) {
      throw new StudioPublicationAdapterRegistryError(
        `Studio publication adapter already registered for domainId "${domainId}".`,
        {
          code: "DUPLICATE_DOMAIN_ID",
          details: {
            domainId,
            existingAdapterId:
              registrations.get(domainId).id,
            incomingAdapterId: adapter.id,
          },
        }
      );
    }

    registrations.set(
      domainId,
      adapter
    );

    return adapter;
  }

  function unregisterPublicationAdapter(domainId) {
    const normalizedDomainId =
      normalizeDomainId(domainId);

    if (!registrations.has(normalizedDomainId)) {
      return false;
    }

    registrations.delete(
      normalizedDomainId
    );

    return true;
  }

  function getPublicationAdapter(domainId) {
    const normalizedDomainId =
      normalizeDomainId(domainId);

    if (!registrations.has(normalizedDomainId)) {
      throw new StudioPublicationAdapterRegistryError(
        `No Studio publication adapter is registered for domainId "${normalizedDomainId}".`,
        {
          code: "UNKNOWN_DOMAIN_ID",
          details: {
            domainId: normalizedDomainId,
          },
        }
      );
    }

    return registrations.get(
      normalizedDomainId
    );
  }

  function hasPublicationAdapter(domainId) {
    const normalizedDomainId =
      normalizeDomainId(domainId);

    return registrations.has(
      normalizedDomainId
    );
  }

  function listPublicationAdapters() {
    return Object.freeze(
      [...registrations.entries()]
        .sort(
          ([leftDomainId], [rightDomainId]) =>
            leftDomainId.localeCompare(
              rightDomainId
            )
        )
        .map(([, adapter]) => adapter)
    );
  }

  function clear() {
    const removedCount =
      registrations.size;

    registrations.clear();

    return removedCount;
  }

  adapters.forEach(
    registerPublicationAdapter
  );

  return Object.freeze({
    registerPublicationAdapter,
    unregisterPublicationAdapter,
    getPublicationAdapter,
    hasPublicationAdapter,
    listPublicationAdapters,
    clear,
  });
}

function normalizeDomainId(domainId) {
  if (
    typeof domainId !== "string"
    || domainId.trim() === ""
  ) {
    throw new StudioPublicationAdapterRegistryError(
      "domainId must be a non-empty string.",
      {
        code: "INVALID_DOMAIN_ID",
        details: {
          domainId,
        },
      }
    );
  }

  return domainId.trim();
}
